import React, { useState, useEffect } from 'react';

import fileDownload from 'js-file-download';
import { Tooltip as ReactTooltip } from 'react-tooltip';

import Button from '@components/common/Button/Button';
import useTranslateLpc from '@components/common/hooks/useTranslateLpc';
import useWindowSize from '@components/common/hooks/useWindowSize';
import Pagination from '@components/common/Pagination/Pagination';
import Spinner from '@components/common/Spinner/Spinner';
import { M_DEVICE } from '@components/lpc/Constants';
import FirstParticipation from '@components/lpc/Pot/MainModule/Activity/Participants/FirstParticipation/FirstParticipation';
import ParticipantCard from '@components/lpc/Pot/MainModule/Activity/Participants/ParticipantCard/ParticipantCard';
import { personRoles } from '@models/PotModel/personRoles';
import { poolContributionType, potPrivacyType, potType } from '@propTypes/potTypes';
import potService from '@services/domain/Lpc/PotService';
import { getUserAvatarSrc } from '@services/utils/UserService';

import css from './Participants.module.scss';

type ParticipantsProps = {
    potRef: string;
    participationNumber: number;
    personRole: string;
    privacy: potPrivacyType;
    poolContribution: poolContributionType;
    pot: potType;
    setOpenParticipate: (boolean) => void;
};

const Participants = ({
    potRef,
    participationNumber,
    personRole,
    privacy,
    poolContribution,
    pot,
    setOpenParticipate,
}: ParticipantsProps) => {
    const { t } = useTranslateLpc(['lpc-pot']);
    const { width } = useWindowSize();

    const [isLoading, setIsLoading] = useState<boolean>(true);

    useEffect(() => {
        width < M_DEVICE ? setItemPerPage(6) : setItemPerPage(8);
    }, [width]);

    const [participantsList, setParticipantsList] = useState([]);
    const [pageRangeDisplayed, setPageRangeDisplayed] = useState<number>(2);

    const [itemPerPage, setItemPerPage] = useState<number>(8);
    const [cursor, setCursor] = useState<number>(0);
    const [pageCount, setPageCount] = useState<number>(null);

    const [isParticipantRefunded, setIsParticipantRefunded] = useState<boolean>(false);

    useEffect(() => {
        setIsParticipantRefunded(false);
        potService
            .getAllParticipants(potRef, cursor, itemPerPage)
            .then((res) => setParticipantsList(res))
            .catch((err) => err)
            .finally(() => setIsLoading(false));
    }, [cursor, itemPerPage, potRef, isParticipantRefunded]);

    const handlePageClick = (page) => {
        setPageRangeDisplayed(2);
        const pageSelected = page.selected;
        const offset = Math.ceil(pageSelected * itemPerPage);
        setCursor(offset);
    };

    useEffect(() => {
        if (participationNumber > 0) {
            setPageCount(Math.ceil(participationNumber / itemPerPage));
        }
    }, [itemPerPage, participationNumber]);

    const onDownloadParticipantList = () => {
        potService
            .getParticipantsList(potRef)
            .then((res) => {
                fileDownload(res.data, `${t('activity.participants-list')}.xlsx`);
            })
            .catch((err) => err);
    };

    const handleParticipantRefunded = () => {
        setIsParticipantRefunded(true);
    };

    return (
        <div className={css.participants}>
            <section className={css.participants__header}>
                <h5>
                    {t(
                        `activity.${pot.category === 'BEREAVEMENT' || pot.category === 'SOLIDARITY_POOL' ? 'donors' : 'participants'}-list`,
                    )}{' '}
                    {participationNumber > 0 && <>({participationNumber})</>}
                </h5>
                {participationNumber > 0 && personRole !== personRoles.PARTICIPANT && (
                    <div data-tip data-tooltip-id="downloadTip">
                        <Button
                            customClass={css.participants__header__cta}
                            icon={'/front-static/icons/action/download-outline-brand.svg'}
                            variant={'secondary'}
                            onClick={() => onDownloadParticipantList()}
                        ></Button>
                        <ReactTooltip
                            hidden={!(participationNumber > 0 && personRole !== personRoles.PARTICIPANT)}
                            id="downloadTip"
                            place="top"
                        >
                            {t('activity.download')}
                        </ReactTooltip>
                    </div>
                )}
            </section>
            {isLoading ? (
                <Spinner customClass={css.participants__spinner} />
            ) : participationNumber > 0 ? (
                <>
                    <div className={css.participants__grid}>
                        {participantsList.map((participant) => (
                            <ParticipantCard
                                key={participant.inviteID}
                                avatar={getUserAvatarSrc(participant)}
                                contribution={participant.contribution}
                                firstName={participant.firstName}
                                lastName={participant.lastName}
                                showContributionAmount={privacy.showContributionAmount}
                                showParticipantName={privacy.showParticipantName}
                                personRole={personRole}
                                isLoading={isLoading}
                                hideContribution={participant.hideContribution}
                                hideName={participant.hideName}
                                potRef={potRef}
                                contributionDate={participant.contributionDate}
                                orderId={participant.orderId}
                                inviteID={participant.inviteID}
                                handleParticipantRefunded={handleParticipantRefunded}
                            />
                        ))}
                    </div>
                    <Pagination
                        handlePageClick={handlePageClick}
                        pageCount={pageCount}
                        pageRangeDisplayed={pageRangeDisplayed}
                        marginPagesDisplayed={1}
                    />
                </>
            ) : (
                <FirstParticipation
                    pot={pot}
                    potRef={potRef}
                    personRole={personRole}
                    poolContribution={poolContribution}
                    setOpenParticipate={(value) => setOpenParticipate(value)}
                />
            )}
        </div>
    );
};

export default Participants;
